import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { credentialsApi } from '../services/api';
import { useAuth } from '../hooks/useAuth';
import { Key, ArrowLeft, Calendar, Tag, Shield, Pencil } from 'lucide-react';

interface CredentialDetail {
  id: string;
  name: string;
  type: string;
  createdAt: string;
  updatedAt: string;
  nodesAccess: string[];
  data: Record<string, string>;
}

export default function CredentialDetailPage() {
  const { id } = useParams<{ id: string }>();
  const { user } = useAuth();
  const [credential, setCredential] = useState<CredentialDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editValues, setEditValues] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);
  const [saveMessage, setSaveMessage] = useState<string | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);

  const canEdit = user?.role === 'admin' || user?.role === 'editor';

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!id) return;
    setSaving(true);
    setSaveMessage(null);
    setSaveError(null);
    try {
      await credentialsApi.setData(id, editValues);
      setSaveMessage('Credential values updated.');
      setEditValues({});
      const response = await credentialsApi.getById(id);
      setCredential(response.data.data.credential);
    } catch (err: any) {
      setSaveError(err.response?.data?.error?.message || 'Failed to update credential');
    } finally {
      setSaving(false);
    }
  };

  useEffect(() => {
    const fetchCredential = async () => {
      try {
        const response = await credentialsApi.getById(id!);
        setCredential(response.data.data.credential);
      } catch (err: any) {
        console.error('Failed to fetch credential:', err);
        setError(err.response?.data?.error?.message || 'Failed to load credential');
      } finally {
        setLoading(false);
      }
    };

    if (id) {
      fetchCredential();
    }
  }, [id]);

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-primary-500 border-t-transparent"></div>
      </div>
    );
  }

  if (error || !credential) {
    return (
      <div className="card text-center py-12">
        <Key className="mx-auto text-gray-600 mb-4" size={48} />
        <h3 className="text-xl font-semibold text-white mb-2">Credential Not Found</h3>
        <p className="text-gray-400 mb-4">{error || 'The credential you are looking for does not exist.'}</p>
        <Link to="/credentials" className="btn btn-primary">
          Back to Credentials
        </Link>
      </div>
    );
  }

  return (
    <div>
      {/* Back button */}
      <Link
        to="/credentials"
        className="inline-flex items-center text-gray-400 hover:text-white mb-6 transition-colors"
      >
        <ArrowLeft size={18} className="mr-2" />
        Back to Credentials
      </Link>

      {/* Header */}
      <div className="card mb-6">
        <div className="flex items-start justify-between">
          <div className="flex items-center">
            <div className="p-3 bg-brand-orange/20 rounded-lg">
              <Key className="text-brand-orange" size={28} />
            </div>
            <div className="ml-4">
              <h1 className="text-2xl font-bold text-white">{credential.name}</h1>
              <p className="text-gray-400">ID: {credential.id}</p>
            </div>
          </div>
          <span className="px-3 py-1 bg-blue-500/20 text-blue-400 rounded-lg text-sm font-medium">
            {credential.type}
          </span>
        </div>

        <div className="grid grid-cols-2 gap-4 mt-6 pt-6 border-t border-gray-700">
          <div className="flex items-center text-gray-400">
            <Calendar size={16} className="mr-2" />
            <span className="text-sm">Created: {formatDate(credential.createdAt)}</span>
          </div>
          <div className="flex items-center text-gray-400">
            <Calendar size={16} className="mr-2" />
            <span className="text-sm">Updated: {formatDate(credential.updatedAt)}</span>
          </div>
        </div>
      </div>

      {/* Credential data */}
      <div className="card mb-6">
        <div className="flex items-center mb-4">
          <Shield className="text-brand-orange mr-2" size={20} />
          <h2 className="text-lg font-semibold text-white">Credential Data (Masked)</h2>
        </div>

        <div className="space-y-3">
          {Object.entries(credential.data).map(([key, value]) => (
            <div
              key={key}
              className="flex items-center justify-between p-3 bg-gray-900 rounded-lg"
            >
              <div className="flex-1 min-w-0">
                <p className="text-gray-400 text-sm">{key}</p>
                <p className="text-white font-mono truncate">{value}</p>
              </div>
            </div>
          ))}
        </div>

        {Object.keys(credential.data).length === 0 && (
          <p className="text-gray-400 text-center py-4">
            No credential data available or data is encrypted.
          </p>
        )}
      </div>

      {/* Update values (admin/editor) */}
      {canEdit && Object.keys(credential.data).length > 0 && (
        <div className="card mb-6">
          <div className="flex items-center mb-4">
            <Pencil className="text-brand-orange mr-2" size={20} />
            <h2 className="text-lg font-semibold text-white">Update Values</h2>
          </div>
          <p className="text-gray-400 text-sm mb-4">
            Enter new values for the fields you want to change. Leave a field blank to keep
            its current value.
          </p>
          <form onSubmit={handleSave} className="space-y-3">
            {Object.keys(credential.data).map((key) => (
              <div key={key}>
                <label className="block text-gray-300 text-sm font-medium mb-1">{key}</label>
                <input
                  type="text"
                  className="input font-mono"
                  placeholder="Leave blank to keep current value"
                  value={editValues[key] || ''}
                  onChange={(e) =>
                    setEditValues((prev) => ({ ...prev, [key]: e.target.value }))
                  }
                  autoComplete="off"
                />
              </div>
            ))}

            {saveError && (
              <div className="p-3 bg-red-500/10 border border-red-500/50 rounded-lg text-red-400 text-sm">
                {saveError}
              </div>
            )}
            {saveMessage && (
              <div className="p-3 bg-green-500/10 border border-green-500/50 rounded-lg text-green-400 text-sm">
                {saveMessage}
              </div>
            )}

            <button
              type="submit"
              disabled={saving || Object.values(editValues).every((v) => !v)}
              className="btn btn-primary disabled:opacity-50"
            >
              {saving ? 'Saving…' : 'Save New Values'}
            </button>
          </form>
        </div>
      )}

      {/* Nodes access */}
      {credential.nodesAccess.length > 0 && (
        <div className="card">
          <div className="flex items-center mb-4">
            <Tag className="text-brand-orange mr-2" size={20} />
            <h2 className="text-lg font-semibold text-white">Used by Nodes</h2>
          </div>

          <div className="flex flex-wrap gap-2">
            {credential.nodesAccess.map((node, index) => (
              <span
                key={index}
                className="px-3 py-1 bg-gray-700 text-gray-300 rounded-lg text-sm"
              >
                {node}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Security notice */}
      <div className="mt-6 p-4 bg-green-500/10 border border-green-500/30 rounded-lg">
        <h4 className="text-green-400 font-medium mb-2">🔒 Security Notice</h4>
        <p className="text-gray-300 text-sm">
          Sensitive credential values (passwords, API keys, tokens) are masked for security.
          The original values are encrypted and stored securely in the database.
        </p>
      </div>
    </div>
  );
}
